import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import AppPagination from "components/common/AppPagination";
import { PortfolioStatsCards } from "components/investments/PortfolioStatsCards";
import { OPPORTUNITIES_PATH } from "lib/opportunitiesNav";
import { InvestmentCancellationBound } from "components/investments/InvestmentCancellationBound";
import { MyInvestmentsList } from "components/investments/MyInvestmentsList";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import { buildQueryString } from "lib/buildQueryString";
import {
  INVESTMENT_STATUS_TABS,
  parseMyInvestmentsListResult,
} from "lib/investmentListHelpers";
import {
  EMPTY_PORTFOLIO_STATS,
  parsePortfolioStats,
} from "lib/portfolioStatsHelpers";
import { useDebouncedValue } from "hooks/useDebouncedValue";

const DEFAULT_LIMIT = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const FILTER_INITIAL_VALUES = {
  page: 1,
  limit: DEFAULT_LIMIT,
  search: "",
  status: "",
  date: "",
};

const TAB_BY_STATUS = Object.fromEntries(
  INVESTMENT_STATUS_TABS.map((tab) => [tab.status, tab.value])
);
const STATUS_BY_TAB = Object.fromEntries(
  INVESTMENT_STATUS_TABS.map((tab) => [tab.value, tab.status])
);

function getActiveTab(status) {
  return TAB_BY_STATUS[status] ?? "all";
}

export default function MyInvestmentsPage() {
  const [filters, setFilters] = useState(FILTER_INITIAL_VALUES);
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const { debounced: debouncedSearch, flush: flushSearch } = useDebouncedValue(
    searchDraft,
    400
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [stats, setStats] = useState(EMPTY_PORTFOLIO_STATS);
  const [listMeta, setListMeta] = useState({ total: 0, limit: DEFAULT_LIMIT, page: 1 });

  const activeTab = getActiveTab(filters.status);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setFilters((prev) => {
      const nextSearch = debouncedSearch.trim();
      if (prev.search === nextSearch) return prev;
      return { ...prev, search: nextSearch, page: 1 };
    });
  }, [debouncedSearch]);

  const totalItems = listMeta.total;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / (listMeta.limit || filters.limit))),
    [totalItems, listMeta.limit, filters.limit]
  );

  const fetchData = useCallback(
    async (signal) => {
      setError(null);
      const qs = buildQueryString(filters);
      const path = qs
        ? `${API_ENDPOINTS.myInvestments}?${qs}`
        : API_ENDPOINTS.myInvestments;

      const resp = await api.get(path, { signal });
      if (!isSuccessResponse(resp)) {
        const message =
          (resp && typeof resp === "object" && (resp.message || resp.error)) ||
          "Failed to load investments";
        throw new Error(message);
      }

      const { investments: rows, total, limit, page } = parseMyInvestmentsListResult(resp);

      setInvestments(rows);
      setStats(parsePortfolioStats(resp));
      setListMeta({
        total,
        limit: limit || filters.limit,
        page: page || filters.page,
      });
    },
    [filters]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchData(controller.signal)
      .catch((e) => {
        if (controller.signal.aborted) return;
        setError(e?.message || "Failed to load investments");
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [fetchData]);

  const handleRetry = useCallback(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchData(controller.signal)
      .catch((e) => setError(e?.message || "Failed to load investments"))
      .finally(() => setLoading(false));
  }, [fetchData]);

  const handleTabChange = useCallback((tab) => {
    setFilters((prev) => ({
      ...prev,
      status: STATUS_BY_TAB[tab] ?? "",
      page: 1,
    }));
  }, []);

  const hasActiveFilters = useMemo(
    () => Boolean(filters.search || filters.status || filters.date),
    [filters]
  );

  const clearFilters = useCallback(() => {
    setSearchDraft("");
    flushSearch();
    setFilters(FILTER_INITIAL_VALUES);
  }, [flushSearch]);

  const clearSearch = useCallback(() => {
    setSearchDraft("");
    flushSearch();
    setFilters((prev) => ({ ...prev, search: "", page: 1 }));
  }, [flushSearch]);

  const handleSearchEnter = useCallback(() => {
    flushSearch();
    setFilters((prev) => ({
      ...prev,
      search: searchDraft.trim(),
      page: 1,
    }));
  }, [flushSearch, searchDraft]);

  return (
    <>
      <Helmet>
        <title>My Investments | Fortify</title>
      </Helmet>

      <div className="bg-secondary/30 min-h-[70vh]">
        <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-12 xl:px-16">
          <Button
            variant="ghost"
            className="d-none mb-6 -ml-2"
            asChild
          >
            <Link to={OPPORTUNITIES_PATH}>
            <ArrowLeft className="mr-2 h-4 w-4" />
                Browse Opportunities
              </Link>
          </Button>

          <PortfolioStatsCards
            loading={loading}
            stats={stats}
            currency={investments[0]?.currency ?? "USD"}
            className="mb-8"
          />

          <Card>
            <CardHeader>
              <CardTitle>My Investments</CardTitle>
              <CardDescription>
                All investments linked to your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <InvestmentCancellationBound onSuccess={handleRetry}>
                {(cancellation) => (
                  <>
                    <MyInvestmentsList
                      loading={loading}
                      error={error}
                      investments={investments}
                      tab={activeTab}
                      onTabChange={handleTabChange}
                      onRetry={handleRetry}
                      searchDraft={searchDraft}
                      onSearchDraftChange={setSearchDraft}
                      onSearchEnter={handleSearchEnter}
                      onClearSearch={clearSearch}
                      onClearFilters={clearFilters}
                      hasActiveFilters={hasActiveFilters}
                      onRequestCancel={cancellation.beginCancellation}
                    />

                    {!loading && !error && totalItems > 0 ? (
                      <AppPagination
                        currentPage={filters.page}
                        totalPages={totalPages}
                        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
                        totalItems={totalItems}
                        pageSize={filters.limit}
                        onPageSizeChange={(limit) =>
                          setFilters((prev) => ({ ...prev, limit, page: 1 }))
                        }
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                      />
                    ) : null}
                  </>
                )}
              </InvestmentCancellationBound>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
